import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LanguageService, TransportMode } from '@hazmatsuite-next/ui';
import { Store } from '@ngrx/store';
import { map, Observable, take, tap } from 'rxjs';
import { createTempPackage, loadPackages, resetPackagingTypes, saveCompositePackage, saveSinglePackage } from '../../store/package-list.actions';
import * as uuid from "uuid";
import { CompositePackagingItemEditorComponent } from './package-editors/composite-packaging-item-editor/composite-packaging-item-editor.component';
import { SinglePackagingItemEditorComponent } from './package-editors/single-packaging-item-editor/single-packaging-item-editor.component';
import { PackagingModel, SinglePackagingModel, EnumPackageType, EasyDgdService, Packages, CompositePackagingModel } from '@hazmatsuite-next/easydgd-lib';
import { selectStepState } from '../../store/easydgd-data.selectors';
import { EasyDgdDataState } from '../../store/easydgd-data.reducer';

@Component({
  selector: 'easydgd-packing-list',
  templateUrl: './packing-list.component.html',
  styleUrls: ['./packing-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackingListComponent implements OnInit {
  @ViewChild('compositePackaging', { static: false }) compositePackaging?: CompositePackagingItemEditorComponent;
  @ViewChild('singlePackaging', { static: false }) singlePackaging?: SinglePackagingItemEditorComponent;

  public stepperItems$ = this.easyDgdStore.select(selectStepState);
  public packagingIsValid = false;

  public packingListResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['PackingListResource']));

  public showConfirmButtonLabel$ = this.store.select(state => state.packageList.tempPackage).pipe(
    map((pkg: PackagingModel) => {
      return !!(pkg as SinglePackagingModel)?.DangerousGoodId;
    })
  );

  public hasPackages$ = this.store.select(state => state.packageList).pipe(
    map(state => {
      return state.packages.CompositePackagingItems.length + state.packages.SinglePackagingItems.length;
    }),
    tap(itemCount => this.formGroup.get('items')?.setValue(itemCount))
  );

  public transportModes = TransportMode;
  public packageTypes = EnumPackageType;

  public formGroup = new FormGroup({
    'items': new FormControl(0, Validators.min(1))
  });

  public data = this.easydgdService.data;

  public activeEditorType?: EnumPackageType;
  public packageEditorOpen = false;
  public packageEditorTitle = '';
  private currentPackage?: PackagingModel;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private easyDgdStore: Store<{ easyDgdState: EasyDgdDataState }>,
    private easydgdService: EasyDgdService,
    private languageService: LanguageService,
    private store: Store<{ packageList: { packages: Packages, tempPackage: PackagingModel } }>) {
    this.store.dispatch(loadPackages());
  }

  ngOnInit(): void {
    const transportMode = this.easydgdService.getTransportMode();
    if (!transportMode || transportMode as TransportMode === TransportMode.Unknown) {
      this.router.navigate(['..'], { relativeTo: this.activatedRoute });
      return;
    }
    this.store.dispatch(resetPackagingTypes())
  }

  selectStep(index: number): void {
    // this.easydgdService.activateStep(index, this.activatedRoute);
  }

  addPackaging(packageType: EnumPackageType, title: string) {
    let pkg: PackagingModel;
    switch (packageType) {
      case EnumPackageType.SinglePacking:
        pkg = new SinglePackagingModel();
        break;
      case EnumPackageType.Tank:
        pkg = new SinglePackagingModel();
        break;
      case EnumPackageType.Bulk:
        pkg = new SinglePackagingModel();
        break;
      case EnumPackageType.CompositePacking:
      case EnumPackageType.CompositePacking_EQ:
      case EnumPackageType.CompositePacking_DG:
      case EnumPackageType.CompositePacking_LQ:
        pkg = new CompositePackagingModel;
        break;
      default:
        pkg = new PackagingModel();
    }

    pkg.PackageType = packageType;
    pkg.InternalId = uuid.v4();
    this.store.dispatch(createTempPackage({ tempPackage: pkg }));
    this.openEditor(packageType, title);
  }

  openEditor(packageType: EnumPackageType, title: string) {
    this.activeEditorType = packageType;
    this.packageEditorOpen = true;
    this.packageEditorTitle = title;
  }

  closePackageEditorModal(): void {
    this.store.dispatch(createTempPackage({ tempPackage: {} }));
    this.packageEditorOpen = false;
    this.singlePackaging?.formGroup?.reset();
    this.compositePackaging?.formGroup?.reset();
  }

  updatePackage(pkg: PackagingModel): void {
    if (this.activeEditorType === EnumPackageType.SinglePacking || this.activeEditorType === EnumPackageType.Bulk || this.activeEditorType === EnumPackageType.Tank) {
      this.currentPackage = pkg as SinglePackagingModel;
    } else {
      this.currentPackage = pkg as CompositePackagingModel;
    }
  }

  savePackage(): void {
    this.packageEditorOpen = false;
    if (this.activeEditorType === EnumPackageType.SinglePacking || this.activeEditorType === EnumPackageType.Bulk || this.activeEditorType === EnumPackageType.Tank) {
      const concretePackage = this.currentPackage as SinglePackagingModel;
      const item: SinglePackagingModel = {
        ...concretePackage,
        DangerousGoodId: concretePackage.DangerousGood?.ID,
        PackageType: this.activeEditorType,
        ProperShippingName: concretePackage.DangerousGood?.DefaultShippingExpression || '',
        DangerousGood: undefined
      }
      this.store.dispatch(saveSinglePackage({ pkg: item }));
      this.singlePackaging?.formGroup?.reset();
    } else {
      const pkg = this.currentPackage as CompositePackagingModel;
      const compositePackage = {
        ...pkg,
        Items: pkg.Items.map(item => ({
          ...item,
          DangerousGood: undefined
        }))
      };
      this.store.dispatch(saveCompositePackage({ pkg: compositePackage }));
      this.compositePackaging?.formGroup.reset();
    }
  }

  markChildAsTouched(): void {
    this.compositePackaging?.markAsTouched();
    this.singlePackaging?.markAsTouched();
  }
}

